import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { Layout } from '../components/Layout/Layout';
import useLogger from '../hooks/useLogger';
import { useNavigation } from '../hooks/useNavigation';
import { setErrorMessage } from '../store/errorSlice';
import { setPhotos, setVideos } from '../store/photoSlice';
import { RootState } from '../store/rootReducer';
import { getDevShotTime } from '../utils/util';
import { flipImageHorizontally } from '../utils/imageProcessing';
import {
  Container,
  Flash,
  InfoPanel,
  Photo,
  WebcamContainer,
  ProcessingOverlay,
  ProcessingContent,
  ProcessingTitle,
  ProcessingSubtitle,
  Spinner,
  InfoText,
  FrameOverlay,
} from './ShotPage.styled';
import { SHOT_CONFIG } from '../constants/shotConfig.ts';

export function ShotPage() {
  const { t } = useTranslation();
  const { photos, videos, shotCount } = useSelector(
    (state: RootState) => state.photo,
    shallowEqual
  );
  const { subFolder, blockCode } = useSelector(
    (state: RootState) => state.block.block || ({} as { subFolder?: string; blockCode?: string })
  );
  const TIME_LEFT = getDevShotTime(blockCode, subFolder); // NOTE: 0초를 포함하기에 +1초로 봐야함.
  const [timeLeft, setTimeLeft] = useState(TIME_LEFT);
  const [shotsLeft, setShotsLeft] = useState(shotCount);

  useEffect(() => {
    photosRef.current = photos;
    videosRef.current = videos;
  }, [photos, videos]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [processState, setProcessState] = useState({
    video: 0,
    shot: 0,
  });
  const [isWebcamReady, setIsWebcamReady] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder[]>([]);
  const chunksRef = useRef<Blob[][]>(Array.from({ length: shotCount }, () => []));
  const photosRef = useRef<string[]>([]);
  const videosRef = useRef<string[]>([]);
  const dispatch = useDispatch();
  const { navigateToChooseFrame } = useNavigation();
  const [flash, setFlash] = useState(false);
  const [photo, setPhoto] = useState<string>(null);
  const logger = useLogger();

  const getCurrentShotNumber = () => {
    return shotCount - shotsLeft + 1;
  };

  const getFrameImagePath = () => {
    if (subFolder !== 'fellin') return null;
    const currentShot = getCurrentShotNumber();
    const frameNumber = ((currentShot - 1) % 4) + 1;
    return `/assets/img/fellin_${frameNumber}.png`;
  };

  const capturePhotoWithDelay = (
    delay: number,
    screenshotDimensions?: {
      width: number;
      height: number;
    }
  ): Promise<string> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const imageSrc = webcamRef.current?.getScreenshot(screenshotDimensions);
        resolve(imageSrc);
      }, delay);
    });
  };

  useEffect(() => {
    if (isWebcamReady && isInitializing) {
      setTimeout(() => {
        setIsInitializing(false);
      }, SHOT_CONFIG.LOADING_TIME * 1000);
    }
  }, [isWebcamReady, isInitializing, dispatch]);

  // NOTE: 비디오 시작, 관리 및 이미지 캡쳐
  useEffect(() => {
    if (isInitializing || shotsLeft === 0) {
      return;
    }

    const mediaRecorder = new MediaRecorder(webcamRef.current.stream, {
      mimeType: 'video/mp4',
    });
    mediaRecorderRef.current.push(mediaRecorder);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current[shotCount - shotsLeft].push(event.data);
      }
    };

    let recorderStop = false;
    let photo = '';
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime === 1) {
          setTimeout(() => {
            setFlash(true);
            SHOT_CONFIG.SHUTTER_SOUND.play();
          }, 100);

          capturePhotoWithDelay(200).then(async (captured) => {
            const finalPhoto = SHOT_CONFIG.MIRROR_MODE
              ? await flipImageHorizontally(captured)
              : captured;
            flushSync(() => setPhoto(finalPhoto));
            photo = finalPhoto;
          });
        }
        if (prevTime <= 0) {
          recorderStop = true;
          mediaRecorder.stop();
          setTimeout(() => {
            dispatch(
              setPhotos({
                photos: [...photosRef.current, photo],
              })
            );
          });
          setShotsLeft((prev) => {
            const newShotsLeft = prev - 1;
            setProcessState((state) => ({
              ...state,
              shot: state.shot + 1,
            }));
            if (newShotsLeft === 0) {
              mediaRecorderRef.current[shotCount - prev].stop();
            }
            return newShotsLeft;
          });
          return TIME_LEFT;
        }
        return prevTime - 1;
      });
    }, 1000);

    mediaRecorder.onstop = () => {
      clearInterval(timer);
      if (recorderStop) {
        console.log('Debug - Video Processing:', {
          currentShot: shotCount - shotsLeft,
          chunksLength: chunksRef.current[shotCount - shotsLeft].length,
        });
        const videoUrl = URL.createObjectURL(
          new Blob(chunksRef.current[shotCount - shotsLeft], { type: 'video/mp4' })
        );

        // 비디오 배열을 업데이트한 후,
        dispatch(setVideos({ videos: [...videosRef.current, videoUrl] }));

        // 프로세스 상태를 업데이트
        Promise.resolve().then(() => {
          setProcessState((state) => {
            const newState = {
              ...state,
              video: state.video + 1 > shotCount ? shotCount : state.video + 1,
            };
            console.log('Debug - Updated Process State:', newState);
            return newState;
          });
        });

        recorderStop = false;
      }
    };

    mediaRecorder.start();
    return () => clearInterval(timer);
  }, [isInitializing, setTimeLeft, shotsLeft, dispatch]);

  // NOTE: 사진, 비디오 최종 확인
  useEffect(() => {
    if (processState.shot === shotCount && processState.video === shotCount) {
      console.log('Debug - Navigation Conditions:', {
        processState,
        photosLength: photos.length,
        videosLength: videos.length,
        shotCount,
      });

      const navigationTimer = setTimeout(() => {
        if (photos.length === shotCount && videos.length === shotCount) {
          console.log('Debug - Navigating with final state:', {
            photos: photos.length,
            videos: videos.length,
          });
          navigateToChooseFrame();
        } else {
          console.log('Debug - Navigation conditions not met:', {
            photos: photos.length,
            videos: videos.length,
            required: shotCount,
          });
        }
      }, 500);

      return () => clearTimeout(navigationTimer);
    }
  }, [processState, photos, videos, navigateToChooseFrame, dispatch]);

  useEffect(() => {
    if (flash) {
      const timer = setTimeout(() => {
        setFlash(null);
      }, SHOT_CONFIG.FLASH_TIME * 1000);

      return () => clearTimeout(timer);
    }
  }, [flash]);

  useEffect(() => {
    if (timeLeft === TIME_LEFT) {
      setPhoto(null);
      return;
    }

    if (photo) {
      const timer = setTimeout(() => {
        setPhoto(null);
      }, SHOT_CONFIG.VIDEO_STOP_TIME * 1000);

      return () => clearTimeout(timer);
    }
  }, [photo, timeLeft]);

  return (
    <Layout>
      <Container>
        <InfoPanel>
          {!isWebcamReady && <InfoText>{t('shot.preparing')}</InfoText>}
          {isWebcamReady && isInitializing && <InfoText>{t('shot.ready')}</InfoText>}
          {!isInitializing && (
            <>
              <InfoText>{t('shot.count', { count: shotsLeft, total: shotCount })}</InfoText>
              <InfoText>{t('shot.time', { time: timeLeft < 0 ? 0 : timeLeft })}</InfoText>
            </>
          )}
        </InfoPanel>
        <Flash flash={flash} />
        {photo && (
          <Photo>
            <img src={photo} />
          </Photo>
        )}
        <WebcamContainer done={processState.shot === shotCount}>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/png"
            screenshotQuality={1}
            videoConstraints={SHOT_CONFIG.VIDEO_CONSTRAINTS}
            onUserMedia={(stream) => {
              const videoTracks = stream.getVideoTracks();
              videoTracks[0].addEventListener('ended', () => {
                setTimeout(() => {
                  dispatch(
                    setErrorMessage({
                      errorMessage: '카메라의 웹 접근이 허용되지 않았어요.',
                      handleErrorMessageMount: () => {
                        logger.errorLog('카메라 에러');
                      },
                    })
                  );
                });
              });

              setIsWebcamReady(true);
            }}
          />
          {getFrameImagePath() && !isInitializing && (
            <FrameOverlay>
              <img src={getFrameImagePath()} alt={`Frame ${getCurrentShotNumber()}`} />
            </FrameOverlay>
          )}
        </WebcamContainer>
        {processState.shot === shotCount && (
          <ProcessingOverlay>
            <ProcessingContent>
              <ProcessingTitle>{t('shot.completed')}</ProcessingTitle>
              <ProcessingSubtitle>{t('shot.waiting')}</ProcessingSubtitle>
              <Spinner />
            </ProcessingContent>
          </ProcessingOverlay>
        )}
      </Container>
    </Layout>
  );
}
